/**
 * Single source of truth for the farm's docker recipe (image / container / volumes
 * and the commands built from them). Used by the Launch / Setup tab (install,
 * cleanup and `docker run` shown for manual setup). The "Restart" / "Restart with
 * update" buttons no longer build a host recipe here — they call the farm agent's
 * docker-managed self-update over the relay (farm-update / farm-restart). Keep the
 * recipe here — do not duplicate image/container/volume literals elsewhere.
 */
export const FARM_IMAGE = 'alkhrameev/mobile-farm:latest';
export const FARM_CONTAINER = 'mobile-farm';
export const FARM_VOLUMES = [
    '/dev/bus/usb:/dev/bus/usb',
    '/var/lib/mobile-farm-adb:/root/.android',
    '/var/lib/mobile-farm-data:/app/farm/data',
    // Persists scrcpy SESSION RECORDINGS (SESSION_RECORDING_DIR=/app/farm/recordings,
    // one sub-folder per device: <name>_<serial>/<date-time>_<operator>.mp4). Unlike the
    // other volumes, recordings live in the CURRENT directory where the command is pasted
    // ($(pwd)/mobile-farm-recordings) — big media files that are meant to be browsed/copied
    // off the host without sudo, and usually on a bigger disk than the root fs. $(pwd)
    // expands in the host shell where the Launch / mf-update command runs (baked at write
    // time into the unquoted mf-update.sh heredoc, same as $HOME did). Survives container
    // recreation / "Restart with update". Regular farms only — VMOS deploys (deploy.vmos.sh
    // / VMOS_DEPLOY_SCRIPT) omit this volume and disable recording via FARM_TYPE=vmos.
    '$(pwd)/mobile-farm-recordings:/app/farm/recordings',
    '/var/lib/mobile-farm-logs:/app/clicker/duo/logs',
    // Persists the duo DEPOSIT cache (processed-deposit hashes + low-balance/no-ops
    // notify timestamps under CACHE_DIR=/app/clicker/duo/cache) across container
    // recreation. The ACCOUNT-NUMBER cache (ACCOUNT_CACHE_DIR=/app/account-cache) is
    // intentionally NOT mounted, so it is ephemeral and wiped on recreate. Mirrors
    // docker/deploy.test-farm.mobile-farm.sh.
    '/var/lib/mobile-farm-cache:/app/clicker/duo/cache',
    // Persists hot-pushed clicker code (/app/clicker-live: versions/ + the `current`
    // symlink, managed by the farm agent). A host path under /var/lib like the others,
    // so it survives container recreation — the "Restart with update" command keeps the
    // live clicker. Mirrors docker/deploy.test-farm.mobile-farm.sh.
    '/var/lib/mobile-farm-clicker:/app/clicker-live',
    // Lets the agent drive the host docker daemon for docker-managed self-update
    // ("Restart with update" spawns a helper that runs FARM_UPDATE_SCRIPT_HOST).
    // Mirrors gen_run_cmd in docker/deploy.test-farm.mobile-farm.sh.
    '/var/run/docker.sock:/var/run/docker.sock',
];
/**
 * Host path of the self-update helper written by {@link farmCreateUpdaterCmd}.
 * `$HOME` stays literal so it expands when the launch command is pasted into the
 * host shell (resolving to the SSH user's home, NOT the panel's). Mirrors
 * UPDATE_SCRIPT_REMOTE in docker/deploy.test-farm.mobile-farm.sh.
 */
export const FARM_UPDATE_SCRIPT_HOST = '$HOME/.mobile-farm/mf-update.sh';
/**
 * Shared `docker run` recipe body (volumes + env list, no leading `docker run`).
 * SINGLE SOURCE shared by {@link farmDockerRun} (the Launch step) and the
 * in-heredoc command emitted by {@link farmCreateUpdaterCmd} so the two can never
 * drift — mirrors gen_run_cmd in docker/deploy.test-farm.mobile-farm.sh.
 *
 * @param opts.key          the farm relay key
 * @param opts.relayHostLine the rendered `-e RELAY_HOST=...` value token (a literal
 *   host for the Launch command, or `"$RELAY_HOST_EFF"` inside mf-update.sh)
 * @param opts.cont         line-continuation suffix: ` \` for the launch command,
 *   ` \\` for lines that must survive inside the mf-update.sh heredoc as a single `\`
 */
function farmRunRecipe(opts) {
    const c = opts.cont;
    const volumeLines = FARM_VOLUMES.map((v) => `  -v ${v}${c}`).join('\n');
    return `docker run -d${c}
  --name ${FARM_CONTAINER}${c}
  --restart unless-stopped${c}
  --network host${c}
  --privileged${c}
  --pid=host${c}
${volumeLines}
  -e RELAY_HOST=${opts.relayHostLine}${c}
  -e RELAY_KEY=${opts.key}${c}
  -e NODE_TLS_REJECT_UNAUTHORIZED=0${c}
  -e FARM_UPDATE_SCRIPT_HOST=${FARM_UPDATE_SCRIPT_HOST}${c}
  -e FARM_UPDATE_IMAGE=${FARM_IMAGE}${c}
  ${FARM_IMAGE}`;
}
/**
 * Install Docker (Ubuntu / Debian) and add the current user to the `docker` group
 * so the subsequent `docker ...` steps (create-updater + `docker run` launch) work
 * without sudo. `$USER` stays literal — it expands on the host at paste time (a bare
 * `$USER` in a TS template literal is fine; only `${` would interpolate). `newgrp
 * docker` activates the new group in the current shell, avoiding a logout/login.
 */
export function farmInstallCmd() {
    return `curl -fsSL https://get.docker.com | sh
sudo usermod -aG docker $USER
newgrp docker`;
}
/**
 * Stop / remove the existing container and image (manual update on the host).
 * Also prunes leftover dangling (`<none>`) images left behind when a new
 * `:latest` is pulled and the previous digest loses its tag.
 */
export function farmCleanupCmd() {
    return `docker stop ${FARM_CONTAINER} || true
docker rm   ${FARM_CONTAINER} || true
docker rmi  ${FARM_IMAGE} || true
docker image prune -f || true`;
}
/**
 * Multiline `docker run -d ...` shown on the Launch / Setup tab. Includes the
 * docker-socket mount + FARM_UPDATE_* env so the farm comes up MANAGED-self-update
 * capable (the panel's "Restart with update" works, no "deployed without
 * managed-update support" banner). The helper it points at
 * (FARM_UPDATE_SCRIPT_HOST) is written by {@link farmCreateUpdaterCmd}.
 *
 * Shares its body with farmCreateUpdaterCmd via {@link farmRunRecipe}, which
 * mirrors gen_run_cmd in docker/deploy.test-farm.mobile-farm.sh — keep all three
 * in sync.
 */
export function farmDockerRun(opts) {
    return farmRunRecipe({ key: opts.key, relayHostLine: opts.relayHost, cont: ' \\' });
}
/**
 * Shell block that writes the host-side self-update helper `~/.mobile-farm/mf-update.sh`
 * (chmod 600) the panel's "Restart with update" relies on. Run on the host BEFORE
 * the Launch `docker run`, so FARM_UPDATE_SCRIPT_HOST points at a real file.
 *
 * Mirrors the `mf-update.sh` generated by docker/deploy.test-farm.mobile-farm.sh
 * (its printf block + gen_run_cmd) — keep in sync with that script and with
 * {@link farmDockerRun}.
 *
 * Escaping: an UNQUOTED heredoc (`<<MFEOF`) is used so `$HOME` expands at paste
 * time (the absolute path is baked into the written file). Every `$` that must be
 * evaluated only when mf-update.sh LATER runs is backslash-escaped (`\$@`, `\$a`,
 * `\$PULL`, `\$IMAGE`, `\${MF_RELAY_HOST:-...}`, `\$RELAY_HOST_EFF`) so it stays
 * literal in the file; the recipe lines end in `\\` so each renders as a single
 * trailing `\` inside the heredoc.
 */
export function farmCreateUpdaterCmd(opts) {
    const recipe = farmRunRecipe({ key: opts.key, relayHostLine: '"\\$RELAY_HOST_EFF"', cont: ' \\\\' });
    return `mkdir -p "$HOME/.mobile-farm"
cat > "$HOME/.mobile-farm/mf-update.sh" <<MFEOF
#!/bin/sh
# mf-update.sh — host-side reboot/update for the mobile-farm container.
# Default: pull then recreate (UPDATE). --no-pull: recreate same image (REBOOT).
set -e
PULL=1
for a in "\\$@"; do
    case "\\$a" in
        --no-pull) PULL=0 ;;
        *) echo "mf-update: unknown arg: \\$a" >&2; exit 2 ;;
    esac
done
IMAGE=${FARM_IMAGE}
if [ "\\$PULL" = 1 ]; then
    echo "mf-update: pulling \\$IMAGE"
    docker pull "\\$IMAGE"
else
    echo "mf-update: --no-pull, recreating with local image \\$IMAGE"
fi
docker rm -f ${FARM_CONTAINER} 2>/dev/null || true
RELAY_HOST_EFF="\\\${MF_RELAY_HOST:-${opts.relayHost}}"
${recipe}
docker image prune -f || true
MFEOF
chmod 600 "$HOME/.mobile-farm/mf-update.sh"`;
}
/**
 * WSL distro the Windows sub-tab installs. The farm runs on Docker Engine INSIDE this
 * distro (not Docker Desktop): Desktop's daemon can't see the distro's
 * ~/.mobile-farm/mf-update.sh, so "Restart with update" would silently do nothing.
 */
export const WSL_DISTRO = 'Ubuntu-24.04';
/** PowerShell (admin): install WSL2 with the Ubuntu distro and usbipd-win for USB phones. */
export function winWslInstallCmd() {
    return `wsl --install -d ${WSL_DISTRO}
winget install --exact dorssel.usbipd-win`;
}
/** Inside Ubuntu: enable systemd so dockerd starts with the distro, then restart WSL. */
export function winSystemdCmd() {
    return `printf '[boot]\\nsystemd=true\\n' | sudo tee /etc/wsl.conf
exit`;
}
/**
 * PowerShell (admin): WSL stops an idle distro, taking the farm down with it. A logon
 * task keeps a process alive in the distro, which also boots dockerd and the container.
 */
export function winKeepAliveCmd() {
    return `wsl --shutdown
schtasks /create /f /tn mobile-farm-wsl /sc onlogon /rl highest /tr "powershell -NoProfile -WindowStyle Hidden -Command wsl.exe -d ${WSL_DISTRO} -u root -- sleep infinity"
schtasks /run /tn mobile-farm-wsl`;
}
/** PowerShell (admin): share a USB phone with WSL; `<BUSID>` comes from `usbipd list`. */
export function winUsbAttachCmd() {
    return `usbipd list
usbipd bind --busid <BUSID>
usbipd attach --wsl --busid <BUSID> --auto-attach`;
}
/**
 * UTM VM the macOS sub-tab sets up. Docker Desktop for Mac can't pass USB devices into
 * a container, so the farm runs the regular Linux recipe inside an Ubuntu VM that UTM
 * (QEMU backend) shares the phones with.
 */
export const MAC_VM_NAME = 'mobile-farm';
/** Mac Terminal: install UTM through Homebrew. */
export function macUtmInstallCmd() {
    return 'brew install --cask utm';
}
/** Mac Terminal: keep the Mac awake. A step of its own: sudo's password prompt eats pasted lines. */
export function macNoSleepCmd() {
    return 'sudo pmset -a sleep 0 disksleep 0 autorestart 1';
}
/**
 * Mac Terminal: start the VM at logon. UTM has no autostart of its own, so a LaunchAgent
 * drives it through utmctl; bootout + bootstrap because legacy `launchctl load` fails with
 * error 5 when the job is already loaded.
 */
export function macAutostartCmd() {
    return `mkdir -p ~/Library/LaunchAgents
cat > ~/Library/LaunchAgents/com.mobile-farm.vm.plist <<'EOF'
<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>Label</key><string>com.mobile-farm.vm</string>
  <key>ProgramArguments</key>
  <array>
    <string>/bin/sh</string><string>-c</string>
    <string>open -ga UTM; sleep 15; /Applications/UTM.app/Contents/MacOS/utmctl start ${MAC_VM_NAME}</string>
  </array>
  <key>RunAtLoad</key><true/>
</dict>
</plist>
EOF
launchctl bootout gui/$(id -u)/com.mobile-farm.vm 2>/dev/null
launchctl bootstrap gui/$(id -u) ~/Library/LaunchAgents/com.mobile-farm.vm.plist`;
}
/** Mac Terminal: Docker Desktop + adb, for the Wi-Fi-adb alternative of the macOS sub-tab. */
export function macDockerDesktopInstallCmd() {
    return 'brew install --cask docker android-platform-tools';
}
/**
 * Mac Terminal, phone on the USB cable: switch adbd to TCP 5555 (lasts until the phone reboots).
 * kill-server frees the phone for the container's adb, which reuses this Mac's key.
 */
export function macAdbTcpipCmd() {
    return `adb devices
adb tcpip 5555
adb kill-server`;
}
/** Docker Desktop step A4: check the phone's adb port, then attach it to the container's adb. */
export function macAdbConnectCmd() {
    return `nc -vz <PHONE_IP> 5555
docker exec ${FARM_CONTAINER} adb connect <PHONE_IP>:5555`;
}
/**
 * `docker run` for Docker Desktop on a Mac: no USB reaches its VM, so phones join over
 * Wi-Fi adb. Bridge net like deploy.vmos.sh; Docker Desktop doesn't share /var/lib, so those
 * mounts become named volumes. The adb dir is the Mac's ~/.android: phones already trust that
 * key from step A2, and the Wi-Fi "Allow debugging?" prompt often never shows.
 * No docker.sock / FARM_UPDATE_*: mf-update.sh would recreate the container with the Linux
 * host-network recipe.
 */
export function macDockerDesktopRun(opts) {
    const volumeLines = FARM_VOLUMES
        .filter((v) => !v.startsWith('/dev/') && !v.startsWith('/var/run/'))
        .map((v) => v.replace(/^\/var\/lib\/mobile-farm-adb:/, '$HOME/.android:'))
        .map((v) => `  -v ${v.replace(/^\/var\/lib\//, '')} \\`)
        .join('\n');
    return `docker run -d \\
  --name ${FARM_CONTAINER} \\
  --restart unless-stopped \\
  --privileged \\
  -p 8000:8000 \\
${volumeLines}
  -e RELAY_HOST=${opts.relayHost} \\
  -e RELAY_KEY=${opts.key} \\
  -e NODE_TLS_REJECT_UNAUTHORIZED=0 \\
  ${FARM_IMAGE}`;
}
/** Inside the VM: print its IP, the address the Mac browser opens the farm UI on. */
export function macVmIpCmd() {
    return 'hostname -I';
}
/**
 * Pre-filled `deploy.vmos.sh` invocation shown on the VMOS sub-tab. `<BOX_IP>`
 * stays a literal placeholder the user fills in with their box IP; the relay key
 * and host are baked in. Mirrors the usage header of docker/deploy.vmos.sh.
 */
export function vmosDeployCommand(opts) {
    return `./deploy.vmos.sh <BOX_IP> \\
  --relay-key ${opts.key} \\
  --relay-host ${opts.relayHost}`;
}
// VERBATIM mirror of docker/deploy.vmos.sh — this is what the Setup tab's
// "Download deploy.vmos.sh" button hands the operator, so a stale copy means people
// deploy an old recipe. It HAD drifted (missing -p 8554:8554, FARM_TYPE=vmos, the
// agent-logs volume and --restart always), so treat it as generated, not edited:
// after changing the script, regenerate this array from it and confirm the two are
// byte-identical. Do not hand-edit lines here, and do not add TS comments between
// them — anything not a quoted line is not part of the script.
//
// Stored as an array of lines joined by \n so the script bytes (which contain
// `${...}`, backticks and backslashes) are preserved verbatim — never put this in
// a template literal.
export const VMOS_DEPLOY_SCRIPT = [
    "#!/usr/bin/env bash",
    "set -euo pipefail",
    "",
    "# deploy.vmos.sh \u2014 deploy the combined \"mobile-farm\" image (ws-scrcpy-web farm",
    "# agent + duo clicker) onto a VMOS Edge \"marsbox\" box and start the container.",
    "#",
    "# The box is a Debian 11 aarch64 appliance running Docker + systemd, reachable",
    "# over ADB as root at <BOX_IP>:5555. Each VMOS Android instance forwards its own",
    "# container adbd (port 5555) to a separate host port chosen dynamically by VMOS",
    "# (not fixed); the box's controlling marsbox adbd is on <BOX_IP>:5555. We drive",
    "# everything over ADB \u2014 pull the image, seed the supervisor target, and",
    "# `docker run` the agent. (The farm agent's MarsboxWatcher then polls the",
    "# supervisor and auto-connects every running instance \u2014 no per-phone seeding.)",
    "#",
    "# Hard-won gotchas this script bakes in (read the comments at each step):",
    "#   - marsbox adbd DROPS :5555 mid-session \u2192 adb_box() reconnects before each call",
    "#   - backgrounded `adb shell` procs die on shell exit \u2192 pull runs via systemd-run",
    "#   - --network host collides with the box's own adbd \u2192 we run on BRIDGE",
    "#",
    "# A successful deploy also writes a host-side updater to the box at",
    "# /container_nswc_lv/mf-update.sh (the VMOS-only reboot/update path \u2014 the panel's",
    "# in-container self-update is a no-op for an immutable-image container). It is",
    "# generated from the SAME `docker run` config as the live deploy (zero drift):",
    "#   update (pull + recreate): systemd-run --unit=mf-update  --collect /container_nswc_lv/mf-update.sh",
    "#   reboot (recreate only):   systemd-run --unit=mf-reboot  --collect /container_nswc_lv/mf-update.sh --no-pull",
    "# Run it via `systemd-run` (systemd owns it on the host) so it survives the",
    "# container dying on `rm -f`; a plain backgrounded adb-shell process would not.",
    "#",
    "# It also installs an enabled systemd unit (/etc/systemd/system/mf-boot.service)",
    "# that recreates the container from the local image on every box boot \u2014 the",
    "# reliable autostart these hard-power-cut boxes need (`--restart always` alone",
    "# can leave a stale container after an unclean shutdown). See \"step 7\".",
    "#",
    "# Usage:",
    "#   ./deploy.vmos.sh <BOX_IP> --relay-key <KEY> [options]",
    "#",
    "# Required:",
    "#   <BOX_IP>            positional, the VMOS box IP (e.g. 192.168.31.126)",
    "#   --relay-key <KEY>   the farm's relay key (UUID) from the panel",
    "#",
    "# Options:",
    "#   --relay-host <URL>  panel relay URL",
    "#                       (default: https://ec2-63-184-174-158.eu-central-1.compute.amazonaws.com)",
    "#   --image <ref>       image to pull/run (default: alkhrameev/mobile-farm:latest)",
    "#   --memory <lim>      container memory limit (default: 4g)",
    "#   --cpus <n>          container CPU limit   (default: 4)",
    "#   -h | --help         show this help",
    "",
    "# \u2500\u2500 defaults \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "RELAY_HOST=\"https://ec2-63-184-174-158.eu-central-1.compute.amazonaws.com\"",
    "IMAGE=\"alkhrameev/mobile-farm:latest\"",
    "MEMORY=\"4g\"",
    "CPUS=\"4\"",
    "BOX_IP=\"\"",
    "RELAY_KEY=\"\"",
    "",
    "# Paths on the box.",
    "RUN_SCRIPT_REMOTE=\"/container_nswc_lv/mf-run.sh\"",
    "# Host-side updater emitted by this deploy (see \"step 6\"): a standalone",
    "# pull+rm+run script that recreates the container from the host docker daemon.",
    "# This is the VMOS-only reboot/update path \u2014 the panel's in-container",
    "# \"restart with update\" is a no-op for an immutable-image container.",
    "UPDATE_SCRIPT_REMOTE=\"/container_nswc_lv/mf-update.sh\"",
    "# systemd SUPERVISOR unit that keeps the container running across boots. `--restart",
    "# always` alone is unreliable on these appliance boxes (hard power-cuts leave",
    "# docker with stale container state \u2192 it silently fails to bring the container",
    "# back), and even a boot oneshot loses it ~30 s in when the box flips its NIC and",
    "# steps the clock. So this is a long-lived loop that recreates the container from",
    "# the local image whenever it's found down (see \"step 7\" for the full rationale).",
    "# It lives on the PERSISTENT root overlay (upperdir on disk), so it survives",
    "# reboots. Verified across real reboots.",
    "BOOT_UNIT_REMOTE=\"/etc/systemd/system/mf-boot.service\"",
    "DATA_DIR_REMOTE=\"/var/lib/mobile-farm-data\"",
    "CONNECTED_TARGETS_REMOTE=\"$DATA_DIR_REMOTE/connected-targets.json\"",
    "CONTAINER=\"mobile-farm\"",
    "",
    "usage() {",
    "    sed -n '34,47p' \"$0\" | sed 's/^# \\{0,1\\}//'",
    "    exit \"${1:-0}\"",
    "}",
    "",
    "err() { printf 'ERROR: %s\\n' \"$*\" >&2; }",
    "step() { printf '\\n==> %s\\n' \"$*\"; }",
    "info() { printf '    %s\\n' \"$*\"; }",
    "",
    "# \u2500\u2500 arg parsing \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "while [[ $# -gt 0 ]]; do",
    "    case \"$1\" in",
    "        --relay-key)  RELAY_KEY=\"${2:-}\";  shift 2 ;;",
    "        --relay-host) RELAY_HOST=\"${2:-}\"; shift 2 ;;",
    "        --image)      IMAGE=\"${2:-}\";      shift 2 ;;",
    "        --memory)     MEMORY=\"${2:-}\";     shift 2 ;;",
    "        --cpus)       CPUS=\"${2:-}\";       shift 2 ;;",
    "        -h|--help)    usage 0 ;;",
    "        -*)           err \"unknown option: $1\"; usage 1 ;;",
    "        *)",
    "            if [[ -z \"$BOX_IP\" ]]; then BOX_IP=\"$1\"; shift",
    "            else err \"unexpected positional argument: $1\"; usage 1; fi",
    "            ;;",
    "    esac",
    "done",
    "",
    "# \u2500\u2500 validation \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "[[ -n \"$BOX_IP\" ]]    || { err \"<BOX_IP> is required\";   usage 1; }",
    "[[ -n \"$RELAY_KEY\" ]] || { err \"--relay-key is required\"; usage 1; }",
    "",
    "if ! [[ \"$BOX_IP\" =~ ^[0-9]+\\.[0-9]+\\.[0-9]+\\.[0-9]+$ ]]; then",
    "    err \"BOX_IP '$BOX_IP' does not look like an IPv4 address\"; exit 1",
    "fi",
    "",
    "# Strip any trailing slash: the agent builds \"<host>/ws-relay\"; a trailing \"/\"",
    "# yields \"//ws-relay\" which the panel won't match \u2192 401.",
    "RELAY_HOST=\"${RELAY_HOST%/}\"",
    "",
    "command -v adb  >/dev/null 2>&1 || { err \"adb not found in PATH (install android-platform-tools)\"; exit 1; }",
    "",
    "ADB_TARGET=\"${BOX_IP}:5555\"",
    "",
    "# Single cleanup trap for all temp files created later (seed targets, run-script,",
    "# host-side updater).",
    "TMP_TARGETS=\"\"",
    "RUN_SCRIPT_LOCAL=\"\"",
    "UPDATE_SCRIPT_LOCAL=\"\"",
    "BOOT_UNIT_LOCAL=\"\"",
    "cleanup() { rm -f \"${TMP_TARGETS:-}\" \"${RUN_SCRIPT_LOCAL:-}\" \"${UPDATE_SCRIPT_LOCAL:-}\" \"${BOOT_UNIT_LOCAL:-}\"; }",
    "trap cleanup EXIT",
    "",
    "echo \"Deploy mobile-farm \u2192 marsbox\"",
    "echo \"  box:        $BOX_IP\"",
    "echo \"  image:      $IMAGE\"",
    "echo \"  relay-host: $RELAY_HOST\"",
    "echo \"  relay-key:  ${RELAY_KEY:0:8}\u2026\"",
    "echo \"  limits:     memory=$MEMORY cpus=$CPUS\"",
    "",
    "# adb_box \u2014 the marsbox adbd routinely drops the :5555 connection when other",
    "# agents/resolvers talk to it. Reconnect (with a few retries) right before each",
    "# `adb -s <target> shell \u2026`, so a dropped link doesn't fail the whole deploy.",
    "# Usage: adb_box <args to `adb -s <target>`...>",
    "adb_box() {",
    "    local i out",
    "    for i in 1 2 3 4 5; do",
    "        adb connect \"$ADB_TARGET\" >/dev/null 2>&1 || true",
    "        if out=\"$(adb -s \"$ADB_TARGET\" \"$@\" 2>&1)\"; then",
    "            printf '%s' \"$out\"",
    "            return 0",
    "        fi",
    "        sleep 2",
    "    done",
    "    printf '%s' \"${out:-}\"",
    "    return 1",
    "}",
    "",
    "# \u2500\u2500 canonical `docker run` config (SINGLE SOURCE OF TRUTH) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# emit_run_cmd prints the one and only `docker run` invocation for the farm",
    "# container. EVERYTHING that starts the container must go through this so the",
    "# live deploy run and the generated host-side updater (mf-update.sh) can never",
    "# drift. Values (image, relay host/key, limits) are resolved into the text here.",
    "#",
    "# Note the run config is HOST-side (bridge net, --privileged, the box's",
    "# /var/lib/mobile-farm-* volumes) \u2014 it is meant to run on the marsbox host",
    "# docker daemon, both during deploy and later via mf-update.sh.",
    "#",
    "# Published ports: 8000 = agent HTTP/UI. 8554 = the agent's RTSP server (mediamtx)",
    "# for camera passthrough \u2014 the box's own supervisor (cbs_go) PULLS the guest camera",
    "# stream from `rtsp://<BOX_IP>:8554/cam_<port>`, so it must be reachable on the box.",
    "#",
    "# No recordings volume on VMOS: FARM_TYPE=vmos disables the session recorder",
    "# (see SessionRecorder / RelayClient.FARM_TYPE), so there is nothing to persist.",
    "#",
    "# The RELAY_HOST token is passed as $1 so the two callers can differ on ONLY",
    "# that field and nothing else (single source for everything else):",
    "#   - live deploy  \u2192 passes the baked literal  '$RELAY_HOST'  (no override)",
    "#   - mf-update.sh \u2192 passes  \"$RELAY_HOST_EFF\"  VERBATIM, so when mf-update.sh",
    "#     runs it honors MF_RELAY_HOST (set by the agent's self-update helper) and",
    "#     falls back to the deploy-baked literal when unset.",
    "#",
    "# Self-update mounts/env (must stay in sync with deploy.test-farm.mobile-farm.sh",
    "# emit_run_cmd \u2014 this is the unified docker-managed self-update mechanism):",
    "#   -v /var/run/docker.sock:/var/run/docker.sock  \u2192 agent can drive host daemon",
    "#   -e FARM_UPDATE_SCRIPT_HOST=...                \u2192 host abs path of mf-update.sh",
    "#   -e FARM_UPDATE_IMAGE=...                      \u2192 image ref for the helper run",
    "# Because emit_run_cmd is reused for mf-update.sh itself, every recreated",
    "# container is started WITH these mounts/env \u2192 the self-update capability",
    "# propagates across recreations.",
    "emit_run_cmd() {",
    "    local relay_host_token=\"$1\"",
    "    cat <<EOF",
    "docker run -d --name $CONTAINER --restart always \\\\",
    "    --network bridge -p 8000:8000 -p 8554:8554 --privileged --memory $MEMORY --cpus $CPUS \\\\",
    "    -v /var/run/docker.sock:/var/run/docker.sock \\\\",
    "    -v /var/lib/mobile-farm-adb:/root/.android \\\\",
    "    -v /var/lib/mobile-farm-data:/app/farm/data \\\\",
    "    -v /var/lib/mobile-farm-agent-logs:/app/farm/logs \\\\",
    "    -v /var/lib/mobile-farm-logs:/app/clicker/duo/logs \\\\",
    "    -v /var/lib/mobile-farm-cache:/app/clicker/duo/cache \\\\",
    "    -v /var/lib/mobile-farm-clicker:/app/clicker-live \\\\",
    "    -e RELAY_HOST=$relay_host_token \\\\",
    "    -e RELAY_KEY='$RELAY_KEY' \\\\",
    "    -e NODE_TLS_REJECT_UNAUTHORIZED=0 \\\\",
    "    -e FARM_UPDATE_SCRIPT_HOST='$UPDATE_SCRIPT_REMOTE' \\\\",
    "    -e FARM_TYPE='vmos' \\\\",
    "    -e FARM_UPDATE_IMAGE='$IMAGE' \\\\",
    "    '$IMAGE'",
    "EOF",
    "}",
    "",
    "# \u2500\u2500 step 1: connectivity \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "step \"Connecting to box over ADB ($ADB_TARGET)\"",
    "adb start-server >/dev/null 2>&1 || true",
    "if ! UNAME=\"$(adb_box shell uname -a)\"; then",
    "    err \"could not reach $ADB_TARGET over ADB.\"",
    "    err \"  - is the box on and on the LAN? ping $BOX_IP\"",
    "    err \"  - is adbd listening on :5555? (another tool may hold it)\"",
    "    exit 1",
    "fi",
    "info \"uname: $UNAME\"",
    "if printf '%s' \"$UNAME\" | grep -qi 'marsbox'; then",
    "    info \"confirmed marsbox box\"",
    "elif printf '%s' \"$UNAME\" | grep -qi 'linux'; then",
    "    info \"WARNING: 'marsbox' not in uname, but it is Linux \u2014 continuing\"",
    "else",
    "    err \"target does not look like a Linux/marsbox box; aborting\"",
    "    exit 1",
    "fi",
    "",
    "# Sanity: docker present on the box.",
    "if ! adb_box shell 'command -v docker >/dev/null 2>&1 && echo ok' | grep -q ok; then",
    "    err \"docker not found on the box (expected at /usr/bin/docker)\"; exit 1",
    "fi",
    "",
    "# \u2500\u2500 step 2: pull the image (must survive adb-shell drops) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# A backgrounded process under `adb shell` is killed when the shell exits, and",
    "# nohup/setsid do NOT help on this adbd. So we launch the pull as a transient",
    "# systemd unit (`systemd-run`) which is owned by systemd, not the shell \u2014 it",
    "# keeps running across adb drops. We then poll `is-active` (reconnecting each",
    "# loop) until the unit finishes and check its Result + that the image exists.",
    "step \"Pulling image on the box: $IMAGE\"",
    "adb_box shell \"systemctl reset-failed mf-pull 2>/dev/null; systemd-run --unit=mf-pull --collect /usr/bin/docker pull '$IMAGE'\" >/dev/null \\",
    "    || { err \"failed to launch pull unit (systemd-run)\"; exit 1; }",
    "",
    "info \"pull running as systemd unit mf-pull; waiting\u2026\"",
    "PULL_WAITED=0",
    "PULL_TIMEOUT=1800   # 30 min \u2014 first pull on a slow LAN/QEMU box can be long",
    "while :; do",
    "    STATE=\"$(adb_box shell 'systemctl is-active mf-pull 2>/dev/null' | tr -d '\\r' | tr -d '[:space:]')\"",
    "    [[ \"$STATE\" != \"active\" && \"$STATE\" != \"activating\" ]] && break",
    "    sleep 5",
    "    PULL_WAITED=$((PULL_WAITED + 5))",
    "    if (( PULL_WAITED % 30 == 0 )); then info \"\u2026still pulling (${PULL_WAITED}s)\"; fi",
    "    if (( PULL_WAITED >= PULL_TIMEOUT )); then",
    "        err \"pull timed out after ${PULL_TIMEOUT}s\"",
    "        adb_box shell \"journalctl -u mf-pull --no-pager -n 30\" >&2 || true",
    "        exit 1",
    "    fi",
    "done",
    "",
    "RESULT=\"$(adb_box shell 'systemctl show mf-pull -p Result --value 2>/dev/null' | tr -d '\\r' | tr -d '[:space:]')\"",
    "HAVE_IMAGE=\"$(adb_box shell \"docker images -q '$IMAGE' 2>/dev/null\" | tr -d '\\r' | tr -d '[:space:]')\"",
    "if [[ \"$RESULT\" != \"success\" || -z \"$HAVE_IMAGE\" ]]; then",
    "    err \"image pull failed (Result=$RESULT, image id='$HAVE_IMAGE'). Last log lines:\"",
    "    adb_box shell \"journalctl -u mf-pull --no-pager -n 40\" >&2 || true",
    "    exit 1",
    "fi",
    "info \"pull OK (image id ${HAVE_IMAGE})\"",
    "",
    "# \u2500\u2500 step 3: seed the supervisor target into the farm device store \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# We seed connected-targets.json with EXACTLY ONE target: the box's marsbox",
    "# supervisor adbd at <BOX_IP>:5555. On boot the agent replays this target,",
    "# resolves it as a marsbox supervisor (recordHost), and from then on its",
    "# MarsboxWatcher polls the supervisor and auto-`adb connect`s every running VMOS",
    "# instance itself. So there is no need to enumerate per-phone host ports here \u2014",
    "# the watcher discovers them dynamically. This runs unconditionally on deploy.",
    "step \"Seeding supervisor target into device store ($ADB_TARGET)\"",
    "TMP_TARGETS=\"$(mktemp)\"",
    "printf '[\"%s\"]\\n' \"$ADB_TARGET\" > \"$TMP_TARGETS\"",
    "# Create the data dir on the box, then push the store file into it.",
    "adb_box shell \"mkdir -p '$DATA_DIR_REMOTE'\" >/dev/null \\",
    "    || { err \"could not create $DATA_DIR_REMOTE on the box\"; exit 1; }",
    "adb connect \"$ADB_TARGET\" >/dev/null 2>&1 || true",
    "adb -s \"$ADB_TARGET\" push \"$TMP_TARGETS\" \"$CONNECTED_TARGETS_REMOTE\" >/dev/null \\",
    "    || { err \"failed to push connected-targets.json to the box\"; exit 1; }",
    "info \"wrote $CONNECTED_TARGETS_REMOTE \u2192 [\\\"$ADB_TARGET\\\"]\"",
    "",
    "# \u2500\u2500 step 4: run the container (BRIDGE, via a pushed run-script) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# WHY BRIDGE, NOT HOST:",
    "#   With --network host the agent's adb client shares the box's network namespace",
    "#   and collides with the box's OWN adbd already bound on 127.0.0.1:5037",
    "#   (\"more than one device and emulator\"), AND adb auto-discovers the box's adbd",
    "#   on 127.0.0.1:5555 as a PHANTOM `emulator-5554` (\"Nexus 4\", undefined model)",
    "#   that pollutes the device list. Bridge gives the container its own netns: no",
    "#   5037 collision, no phantom. The phones are reached via the box LAN IP",
    "#   <BOX_IP>:<instance-port> (each instance's host port is assigned dynamically",
    "#   from the box's docker port mapping), which IS reachable from a bridge",
    "#   container (verified).",
    "#   (If someone really needs --network host, they'd also have to set",
    "#    -e ANDROID_ADB_SERVER_PORT=5038 to dodge the 5037 collision.)",
    "#",
    "# We push a run-script and execute it (rather than a long inline `adb shell`):",
    "# `docker run -d` daemonizes, so the container survives the shell exit.",
    "step \"Starting container '$CONTAINER' (network=bridge)\"",
    "RUN_SCRIPT_LOCAL=\"$(mktemp)\"",
    "{",
    "    printf '#!/bin/sh\\nset -e\\n'",
    "    printf 'docker rm -f %s 2>/dev/null || true\\n' \"$CONTAINER\"",
    "    emit_run_cmd \"'$RELAY_HOST'\"",
    "} > \"$RUN_SCRIPT_LOCAL\"",
    "",
    "adb connect \"$ADB_TARGET\" >/dev/null 2>&1 || true",
    "adb -s \"$ADB_TARGET\" push \"$RUN_SCRIPT_LOCAL\" \"$RUN_SCRIPT_REMOTE\" >/dev/null \\",
    "    || { err \"failed to push run-script to the box\"; exit 1; }",
    "if ! RUN_OUT=\"$(adb_box shell \"sh '$RUN_SCRIPT_REMOTE'\")\"; then",
    "    err \"docker run failed:\"; printf '%s\\n' \"$RUN_OUT\" >&2; exit 1",
    "fi",
    "info \"container id: $(printf '%s' \"$RUN_OUT\" | tr -d '\\r' | tail -n1 | cut -c1-12)\"",
    "",
    "# \u2500\u2500 step 5: verify & report \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "step \"Verifying\"",
    "sleep 5",
    "STATUS=\"$(adb_box shell \"docker ps --filter name=^/${CONTAINER}\\$ --format '{{.Status}}'\" | tr -d '\\r')\"",
    "if ! printf '%s' \"$STATUS\" | grep -qi '^Up'; then",
    "    err \"container is not Up (status: '${STATUS:-missing}'). Recent logs:\"",
    "    adb_box shell \"docker logs $CONTAINER --tail 40 2>&1\" >&2 || true",
    "    exit 1",
    "fi",
    "info \"status: $STATUS\"",
    "",
    "# \u2500\u2500 step 6: emit the host-side updater (mf-update.sh) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# WHY: on the box the agent runs as a manually-started container with an",
    "# IMMUTABLE image. The panel's \"restart with update\" self-update is a no-op \u2014",
    "# a container cannot pull a new image and recreate itself. The real update path",
    "# is the HOST docker daemon doing pull + rm -f + run. We bake that into a",
    "# standalone script on the box so it can be triggered from the agent's own",
    "# terminal (over adb \u2192 systemd-run, which survives the container's death).",
    "#",
    "# It is generated from the SAME emit_run_cmd as the live run above \u2192 zero drift.",
    "# It runs ON THE HOST where the relay key/host are already not secret, so the",
    "# resolved values are baked in. The repo itself never contains a real key.",
    "#",
    "#   default               : pull <image>, then rm -f + run  (UPDATE)",
    "#   --no-pull             : rm -f + run with the same local image  (plain REBOOT)",
    "#   idempotent            : rm -f tolerates an absent container; safe to re-run",
    "step \"Writing host-side updater \u2192 $UPDATE_SCRIPT_REMOTE\"",
    "UPDATE_SCRIPT_LOCAL=\"$(mktemp)\"",
    "{",
    "    printf '#!/bin/sh\\n'",
    "    printf '# mf-update.sh \u2014 host-side reboot/update for the mobile-farm container.\\n'",
    "    printf '# GENERATED by docker/deploy.vmos.sh; do not edit by hand (regenerated on each deploy).\\n'",
    "    printf '# Default: docker pull then recreate (UPDATE). --no-pull: recreate same image (REBOOT).\\n'",
    "    printf '# Trigger from the agent terminal so it survives the container dying on rm -f:\\n'",
    "    printf '#   adb -s %s:5555 shell \"systemd-run --unit=mf-update --collect %s\"\\n' \"$BOX_IP\" \"$UPDATE_SCRIPT_REMOTE\"",
    "    printf '#   adb -s %s:5555 shell \"systemd-run --unit=mf-reboot --collect %s --no-pull\"\\n' \"$BOX_IP\" \"$UPDATE_SCRIPT_REMOTE\"",
    "    printf 'set -e\\n'",
    "    printf 'PULL=1\\n'",
    "    printf 'for a in \"$@\"; do\\n'",
    "    printf '    case \"$a\" in\\n'",
    "    printf '        --no-pull) PULL=0 ;;\\n'",
    "    printf '        *) echo \"mf-update: unknown arg: $a\" >&2; exit 2 ;;\\n'",
    "    printf '    esac\\n'",
    "    printf 'done\\n'",
    "    printf 'IMAGE=%s\\n' \"'$IMAGE'\"",
    "    printf 'if [ \"$PULL\" = 1 ]; then\\n'",
    "    printf '    echo \"mf-update: pulling $IMAGE\"\\n'",
    "    printf '    docker pull \"$IMAGE\"\\n'",
    "    printf 'else\\n'",
    "    printf '    echo \"mf-update: --no-pull, recreating with local image $IMAGE\"\\n'",
    "    printf 'fi\\n'",
    "    printf 'docker rm -f %s 2>/dev/null || true\\n' \"$CONTAINER\"",
    "    # RELAY_HOST is overridable at self-update time: the agent's self-update",
    "    # helper container sets MF_RELAY_HOST to the panel's current address; a",
    "    # direct host run leaves it unset \u2192 the deploy-baked literal is used.",
    "    # The ${MF_RELAY_HOST:-...} / $RELAY_HOST_EFF are written VERBATIM (evaluated",
    "    # when mf-update.sh runs); only the baked default is expanded here, now.",
    "    printf 'RELAY_HOST_EFF=\"${MF_RELAY_HOST:-%s}\"\\n' \"$RELAY_HOST\"",
    "    emit_run_cmd '\"$RELAY_HOST_EFF\"'",
    "    # After recreate, clear OUR docker garbage on the space-constrained box.",
    "    #",
    "    # NEVER `-a` here. `docker image prune -af` removes every image no RUNNING",
    "    # container references \u2014 and on a VMOS box that includes the Android ROM",
    "    # image (`vcloud_android15_edge_*:latest`) whenever the instances happen to be",
    "    # powered off. Deleting it bricks the box: cbs_go then fails every start with",
    "    # \"No such image: vcloud_android15_edge_\u2026\", the instances can never come back,",
    "    # and the ROM has to be re-imported from the vendor. Observed on a live box.",
    "    #",
    "    # Dangling-only prune achieves the actual intent: after `docker pull`, the",
    "    # superseded mobile-farm layers are untagged, so they are exactly what gets",
    "    # cleared, while every TAGGED image (the ROM above all) is untouched.",
    "    printf 'docker image prune -f || true\\n'",
    "    # Likewise no `docker container prune`: on a VMOS box a stopped instance can be",
    "    # a stopped container, and pruning it would destroy the instance. Nothing of ours",
    "    # accumulates without it \u2014 the old agent container is removed by the `docker rm -f`",
    "    # above, and the self-update helper runs `docker run -d --rm` (see",
    "    # DockerSelfUpdater.ts), so it deletes itself. A blanket prune could therefore only",
    "    # ever destroy someone else's container.",
    "} > \"$UPDATE_SCRIPT_LOCAL\"",
    "",
    "adb connect \"$ADB_TARGET\" >/dev/null 2>&1 || true",
    "if adb -s \"$ADB_TARGET\" push \"$UPDATE_SCRIPT_LOCAL\" \"$UPDATE_SCRIPT_REMOTE\" >/dev/null \\",
    "   && adb_box shell \"chmod +x '$UPDATE_SCRIPT_REMOTE'\" >/dev/null; then",
    "    info \"updater written + chmod +x ($UPDATE_SCRIPT_REMOTE)\"",
    "else",
    "    err \"could not write host-side updater to $UPDATE_SCRIPT_REMOTE (deploy itself is fine)\"",
    "fi",
    "",
    "# \u2500\u2500 step 7: install the boot-autostart SUPERVISOR unit (mf-boot.service) \u2500\u2500\u2500\u2500\u2500\u2500\u2500",
    "# WHY a supervisor loop (not `--restart always`, not a boot oneshot) \u2014 all three",
    "# facts below were observed on a real marsbox and verified across real reboots:",
    "#   1. `--restart always` is unreliable: the box's shutdown SIGTERMs the container",
    "#      (exits 0) and docker flags it daemon-stopped; on the next boot dockerd does",
    "#      NOT restore it (no mobile-farm mention in `journalctl -u docker -b`).",
    "#   2. A plain boot oneshot recreates the container once at boot \u2014 but ~30 s later",
    "#      the box flips its primary NIC (eth0 \u2192 mac0, its \"Wired connection 1\") and",
    "#      NTP steps the clock from a ~2022 RTC to real time. That transient SIGTERMs",
    "#      the freshly-started agent (exit 0), and again docker does not bring it back.",
    "#   3. So the container MUST be re-asserted after the network/clock settle. A",
    "#      long-lived supervisor that recreates it whenever it's found down handles",
    "#      the 30 s transient AND any later death. (Observed: container blips once",
    "#      around the NIC flip, supervisor recreates it in the now-stable env, stays up.)",
    "#",
    "# Ordering/enablement gotchas (also verified):",
    "#   - Hang it off docker.service (WantedBy=docker.service), NOT multi-user.target:",
    "#     on a cold/power boot this box brings docker up via SOCKET activation, and",
    "#     multi-user.target.wants is not honored in time \u2014 a unit wanted only by",
    "#     multi-user.target silently never starts (ConditionResult=no, no journal).",
    "#   - Do NOT couple to container_nswc_lv.mount / RequiresMountsFor: that mount is",
    "#     an x-systemd.automount and coupling to it makes systemd drop the unit from",
    "#     the boot transaction. docker.service already RequiresMountsFor the data-root.",
    "#   - OnUnitActiveSec systemd timers arm to `infinity` here \u2014 hence a plain",
    "#     Restart=always loop, not a timer.",
    "#   - Unit must be 0644 (adb `cat >`/push leaves it world-writable \u2192 systemd warns).",
    "# The loop only runs mf-update.sh when the container is NOT already running, so the",
    "# steady state is a cheap `docker inspect` every 20 s.",
    "step \"Installing boot-autostart supervisor unit \u2192 $BOOT_UNIT_REMOTE\"",
    "BOOT_UNIT_LOCAL=\"$(mktemp)\"",
    "cat > \"$BOOT_UNIT_LOCAL\" <<EOF",
    "[Unit]",
    "Description=mobile-farm container autostart + supervisor (VMOS boot self-heal)",
    "Requires=docker.service",
    "After=docker.service",
    "StartLimitIntervalSec=0",
    "",
    "[Service]",
    "Restart=always",
    "RestartSec=15",
    "ExecStart=/bin/sh -c \"while :; do docker inspect -f {{.State.Running}} $CONTAINER 2>/dev/null | grep -qx true || /bin/sh $UPDATE_SCRIPT_REMOTE --no-pull; sleep 20; done\"",
    "",
    "[Install]",
    "WantedBy=docker.service",
    "EOF",
    "",
    "adb connect \"$ADB_TARGET\" >/dev/null 2>&1 || true",
    "if adb -s \"$ADB_TARGET\" push \"$BOOT_UNIT_LOCAL\" \"$BOOT_UNIT_REMOTE\" >/dev/null \\",
    "   && adb_box shell \"chmod 644 '$BOOT_UNIT_REMOTE' && systemctl daemon-reload && systemctl enable --now mf-boot.service\" >/dev/null 2>&1; then",
    "    info \"supervisor unit installed + enabled (keeps the container up across reboots + NIC/clock flips)\"",
    "else",
    "    err \"could not install/enable mf-boot.service (deploy itself is fine; --restart always still applies)\"",
    "fi",
    "",
    "# Give the agent a moment to connect to the relay (and replay the seeded",
    "# supervisor target, after which its watcher auto-connects the instances).",
    "sleep 8",
    "LOGS=\"$(adb_box shell \"docker logs $CONTAINER 2>&1\" || true)\"",
    "RELAY_OK=\"$(printf '%s' \"$LOGS\" | grep -c 'Relay connected' || true)\"",
    "DEV_CONNECTED=\"$(printf '%s' \"$LOGS\" | grep -Ec 'reconnect .*\u2192 connected|\u2192 connected' || true)\"",
    "",
    "echo",
    "echo \"\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\"",
    "echo \"  Deploy summary\"",
    "echo \"\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\"",
    "echo \"  box:            $BOX_IP\"",
    "echo \"  container:      $CONTAINER ($STATUS)\"",
    "echo \"  image id:       $HAVE_IMAGE\"",
    "echo \"  relay key:      ${RELAY_KEY:0:8}\u2026\"",
    "echo \"  relay connected:$([[ \"$RELAY_OK\" -gt 0 ]] && echo ' yes' || echo ' NOT YET (check logs)')\"",
    "echo \"  seeded target:  $ADB_TARGET (supervisor)\"",
    "echo \"  devices connected (log): $DEV_CONNECTED\"",
    "echo \"  updater:        $UPDATE_SCRIPT_REMOTE\"",
    "echo \"  boot autostart: $BOOT_UNIT_REMOTE (enabled)\"",
    "echo \"\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\"",
    "if [[ \"$RELAY_OK\" -eq 0 ]]; then",
    "    info \"relay not confirmed yet; tail logs with:\"",
    "    info \"  adb -s $ADB_TARGET shell docker logs -f $CONTAINER\"",
    "fi",
    "echo",
    "info \"host-side reboot/update from the agent terminal (VMOS-only \u2014 the panel\"",
    "info \"self-update is a no-op for the containerized farm):\"",
    "info \"  update (pull + recreate):\"",
    "info \"    adb -s $ADB_TARGET shell \\\"systemd-run --unit=mf-update --collect $UPDATE_SCRIPT_REMOTE\\\"\"",
    "info \"  reboot (recreate, no pull):\"",
    "info \"    adb -s $ADB_TARGET shell \\\"systemd-run --unit=mf-reboot --collect $UPDATE_SCRIPT_REMOTE --no-pull\\\"\"",
    "",
    "# Clear OUR docker garbage on the space-constrained box \u2014 DANGLING IMAGES ONLY.",
    "# See the long note in the mf-update.sh generator above: `docker image prune -af`",
    "# deletes the VMOS Android ROM image whenever the instances are powered off, which",
    "# permanently breaks every instance on the box (\"No such image:",
    "# vcloud_android15_edge_\u2026\"). A dangling-only prune still clears the superseded",
    "# mobile-farm layers left by `docker pull`, and never touches a tagged image.",
    "# `docker container prune` is deliberately gone too: a stopped VMOS instance can be",
    "# a stopped container, and nothing of ours accumulates anyway (old agent container \u2192",
    "# `docker rm -f`; self-update helper \u2192 `docker run -d --rm`). Non-fatal \u2014 the",
    "# container is already up, so a prune hiccup must not fail the deploy.",
    "adb_box shell \"docker image prune -f || true\" >/dev/null 2>&1 || true",
    "",
    "echo \"Done.\"",
].join("\n");
